import React, { useState, useEffect, useCallback, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Dropdown from "react-bootstrap/Dropdown";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { jwtDecode } from "jwt-decode";
import {
  createTemplate,
  updateTemplate,
  uploadImage,
  getUserPracticeById,
  getDisciplineCodes,
  publishTemplate,
  createLibraryTemplate,
  updateLibraryTemplate,
} from "../../api";
import { FaCheck, FaTimes } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./TemplateBuilder.css";
import NoteRenderer, {
  RICH_MEMO_QUILL_MODULES,
  RICH_MEMO_QUILL_FORMATS,
  normalizeRichMemoHtml,
  sanitizeRichMemoHtml,
} from "./NoteRenderer";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2, 10);

const SIDEBAR_ELEMENTS = [
  { type: "title",       icon: "H",    label: "Header" },
  { type: "divider",     icon: "—",    label: "Divider Line" },
  { type: "text",        icon: "A",    label: "Text Field" },
  { type: "memo",        icon: "≡",    label: "Memo / Notes" },
  { type: "rich_memo",   icon: "¶",    label: "Rich Memo" },
  { type: "checkbox",    icon: "☑",   label: "Checkboxes" },
  { type: "image",       icon: "🖼",   label: "Image" },
  { type: "photo_upload", icon: "📷",  label: "Photo Upload" },
  { type: "radio",       icon: "◉",   label: "Radio (Single)" },
  { type: "yes_no",      icon: "Y/N",  label: "Yes / No" },
  { type: "numeric",     icon: "123",  label: "Numeric" },
  { type: "pain_scale",  icon: "⟋",    label: "Scale" },
  { type: "dropdown",    icon: "▾",   label: "Dropdown" },
  { type: "date",        icon: "📅",   label: "Date" },
  { type: "static_text", icon: "T",    label: "Static Text" },
  { type: "spacer",      icon: "↕",   label: "Spacer" },
];

const COL_OPTIONS = [
  { span: 12, label: "1/1" },
  { span: 6,  label: "1/2" },
  { span: 4,  label: "1/3" },
  { span: 3,  label: "1/4" },
];

const INTERACTIVE_TYPES = ["text", "memo", "rich_memo", "checkbox", "radio", "yes_no", "numeric", "pain_scale", "dropdown", "date", "image", "photo_upload"];

const FIELD_TYPE_LABELS = {
  title: "Header", divider: "Divider", text: "Text Field", memo: "Memo",
  rich_memo: "Rich Memo",
  checkbox: "Checkboxes", image: "Image", photo_upload: "Photo Upload", radio: "Radio (Single)", yes_no: "Yes / No",
  numeric: "Numeric", pain_scale: "Scale", dropdown: "Dropdown", date: "Date",
  static_text: "Static Text", spacer: "Spacer", group: "Group / Section",
};

// Types SyncTo can route a dictation into. Mirrors TEMPLATE_ALLOWLIST_TYPES in
// backend/src/syncToService.js — a field of any other type has nothing for the
// AI to fill, so it gets no AI settings.
const AI_TYPES = ["text", "memo", "rich_memo", "checkbox", "yes_no", "radio", "dropdown", "numeric", "pain_scale", "date"];

// Types whose default value is prose and can therefore be kept as the floor of
// the AI's output. A checkbox default is an array of options, not text.
const AI_KEEP_TYPES = ["text", "memo", "rich_memo"];

// ─── Legacy AI markers ────────────────────────────────────────────────────────
// Before fields had their own AI settings, authors expressed them inside the
// default value with markers: "{use default}" on the first line to keep the
// text, and "{from = to}" pairs at the top for term substitutions. The backend
// still honours those, so nothing migrates automatically — but where a field
// has them we offer to move them into the real fields. Both regexes match
// backend/src/syncToService.js.
const USE_DEFAULT_MARKER  = /^\s*\{\s*use\s+default\s*\}\s*\n?/i;
const REPLACEMENT_PAIR_RE = /\{\s*([^{}=\n]+?)\s*=\s*([^{}=\n]+?)\s*\}/g;

// Splits the leading run of pure "{from = to}" lines off the top of a value.
// A line mixing pairs with other words is guidance, not a replacement block.
function parseReplacementBlock(text) {
  if (!text || typeof text !== "string") return { replacements: [], remaining: text || "" };
  const lines = text.split("\n");
  const replacements = [];
  let consumed = 0;
  for (const line of lines) {
    const matches = [...line.matchAll(REPLACEMENT_PAIR_RE)];
    if (matches.length === 0) break;
    if (line.replace(REPLACEMENT_PAIR_RE, "").trim().length > 0) break;
    for (const m of matches) {
      const from = m[1].trim();
      const to = m[2].trim();
      if (from && to) replacements.push({ from, to });
    }
    consumed++;
  }
  if (replacements.length === 0) return { replacements: [], remaining: text };
  return { replacements, remaining: lines.slice(consumed).join("\n").replace(/^\n+/, "") };
}

/** Does this default value still carry legacy AI markers? */
export function hasLegacyMarkers(defaultValue) {
  if (typeof defaultValue !== "string" || !defaultValue.trim()) return false;
  const { replacements, remaining } = parseReplacementBlock(defaultValue);
  return replacements.length > 0 || USE_DEFAULT_MARKER.test(remaining);
}

/**
 * Converts a marker-carrying default value into the dedicated AI fields.
 *
 * The two marker forms mean different things, so they convert differently:
 *   {use default} — the remaining text IS default content. It stays in
 *                   defaultValue and the field switches to "keep" mode.
 *   no marker     — the text was only ever a listening cue. It moves into
 *                   aiInstructions and defaultValue is cleared, which is what
 *                   stops it being pre-filled into the practitioner's note.
 */
export function markersToAiFields(defaultValue) {
  const { replacements, remaining } = parseReplacementBlock(defaultValue || "");
  const keeps = USE_DEFAULT_MARKER.test(remaining);
  const body = remaining.replace(USE_DEFAULT_MARKER, "").trim();

  const updates = { aiMode: keeps ? "keep" : "guidance" };
  if (replacements.length) updates.aiReplacements = replacements;

  if (keeps) {
    updates.defaultValue = body;
  } else {
    updates.aiInstructions = body;
    updates.defaultValue = "";
  }
  return updates;
}

function makeElement(type) {
  const base = {
    id: uid(), kind: "element", type,
    label: "", defaultValue: null, imageUrl: null, colSpan: 12,
    required: false,    // DATA-12 — boolean, not string
    placeholder: "",    // DATA-13
  };
  if (type === "rich_memo")   return { ...base, defaultValue: "", rows: 4 };
  if (type === "checkbox")    return { ...base, options: [], defaultValue: [] };
  if (type === "radio")       return { ...base, options: [], defaultValue: null };
  if (type === "dropdown")    return { ...base, options: [], defaultValue: null };
  if (type === "image")         return { ...base, imageWidth: 100, defaultValue: null };
  if (type === "photo_upload")  return { ...base, defaultValue: null };
  if (type === "numeric")     return { ...base, unit: "", placeholder: "" };
  if (type === "pain_scale")  return { ...base, defaultValue: 0 };
  if (type === "yes_no")      return { ...base, defaultValue: null };
  if (type === "date")        return { ...base, defaultValue: null, placeholder: "" };
  if (type === "static_text") return { ...base, label: "Static text\u2026" };
  if (type === "spacer")      return { ...base, height: 24 };
  return base;
}

function makeGroup() {
  return { id: uid(), kind: "group", groupTitle: "", colSpan: 12, children: [] };
}

function makePage(n) {
  return { pageId: uid(), pageTitle: `Page ${n}`, items: [] };
}

/** Convert legacy notes[] → single-page pages[] so old templates load in the builder */
export function notesToPages(notes) {
  return [{
    pageId: uid(),
    pageTitle: "Page 1",
    items: (notes || []).map((n, i) => {
      const stableId = n.elementId || `legacy-${i}`;  // stable index-based ID for old templates without elementId

      if (n.sessionsFieldType === "multiselect") {
        return {
          id: stableId, kind: "element", type: "checkbox",
          label: n.sessionsSection || "",
          options: Array.isArray(n.sessionsSectionNotes)
            ? n.sessionsSectionNotes
            : (n.sessionsSectionNotes?.availableOptions || []),
          defaultValue: n.sessionsSectionNotes?.selectedOptions || [],
          colSpan: n.colSpan || 12,
          required: !!n.required,
          placeholder: n.placeholder || "",
        };
      }
      if (n.sessionsFieldType === "image") {
        return {
          id: stableId, kind: "element", type: "image",
          label: n.sessionsSection || "",
          imageUrl: n.sessionsImageURL || null,
          imageWidth: n.imageWidth || 100,
          defaultValue: null,
          colSpan: n.colSpan || 12,
          required: !!n.required,
          placeholder: n.placeholder || "",
        };
      }
      // radio and dropdown — options-bearing types
      if (n.sessionsFieldType === "radio" || n.sessionsFieldType === "dropdown") {
        return {
          id: stableId, kind: "element", type: n.sessionsFieldType,
          label: n.sessionsSection || "",
          options: n.sessionsSectionNotes?.availableOptions || [],
          defaultValue: n.sessionsSectionNotes?.selectedOptions || [],
          colSpan: n.colSpan || 12,
          required: !!n.required,
          placeholder: n.placeholder || "",
        };
      }
      // numeric — has unit field
      if (n.sessionsFieldType === "numeric") {
        return {
          id: stableId, kind: "element", type: "numeric",
          label: n.sessionsSection || "",
          defaultValue: typeof n.sessionsSectionNotes === "string" ? n.sessionsSectionNotes : "",
          unit: n.unit || "",
          colSpan: n.colSpan || 12,
          required: !!n.required,
          placeholder: n.placeholder || "",
        };
      }
      // Default: text, memo (textarea), yes_no, pain_scale, date
      return {
        id: stableId, kind: "element",
        type: n.sessionsFieldType === "textarea" ? "memo"
            : n.sessionsFieldType || "text",
        label: n.sessionsSection || "",
        defaultValue: typeof n.sessionsSectionNotes === "string" ? n.sessionsSectionNotes : null,
        imageUrl: null,
        colSpan: n.colSpan || 12,
        required: !!n.required,
        placeholder: n.placeholder || "",
      };
    }),
  }];
}

// ─── AddToGroupPicker ────────────────────────────────────────────────────────

function AddToGroupPicker({ onAdd }) {
  const [type, setType] = useState("text");
  return (
    <div className="d-flex gap-1 align-items-center mt-2" onClick={e => e.stopPropagation()}>
      <Form.Select
        size="sm"
        value={type}
        onChange={e => setType(e.target.value)}
        style={{ maxWidth: "160px" }}
      >
        {SIDEBAR_ELEMENTS.map(el => (
          <option key={el.type} value={el.type}>{el.label}</option>
        ))}
      </Form.Select>
      <Button variant="outline-secondary" size="sm" onClick={() => onAdd(type)}>
        + Add
      </Button>
    </div>
  );
}

// ─── TemplateBuilder ─────────────────────────────────────────────────────────

// `scope` decides which collection this builder writes to:
//   "practice" (default) — a normal practice template
//   "system"             — a library template, SystemAdmin only. The practice
//                          picker disappears (a library template belongs to no
//                          practice) and saves go to the /templates/library
//                          routes. Publish there only flips the status: the
//                          version-snapshot route refuses library templates by
//                          design, since versions belong to the practice copy.
export function TemplateBuilder({ mode, template, show, onHide, onSaved, scope = "practice" }) {
  const isLibrary = scope === "system";
  const [meta, setMeta] = useState({
    templateTitle: "",
    templateDescription: "",
    templateStatus: "Active",
    templateInitial: "initial",
    templateLanguage: "en",
    templatePracticeId: [],
    // [{ disciplineCode, disciplineName }]. Empty means "all disciplines";
    // the library is filtered on it, so a tagged template only shows to
    // practices of that profession.
    templateDisciplines: [],
  });
  const [pages, setPages] = useState(() => [makePage(1)]);
  const [activePageIdx, setActivePageIdx] = useState(0);
  const [editingPageIdx, setEditingPageIdx] = useState(null);
  const [editingPageDraft, setEditingPageDraft] = useState("");
  const [pracName, setPracName] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  // Every discipline the platform knows, flattened from the grouped
  // /disciplines response and de-duplicated by code.
  const [disciplineOptions, setDisciplineOptions] = useState([]);
  // Until the user touches the picker, a new practice template follows the
  // discipline(s) of the practices ticked below it.
  const [disciplinesTouched, setDisciplinesTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [closeAlert, setCloseAlert] = useState(false);
  const [successAlert, setSuccessAlert] = useState("");
  const [pendingImages, setPendingImages] = useState({}); // id → { file, preview }
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Find selected item (searches top-level + group children)
  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null;
    const page = pages[activePageIdx] || pages[0];
    for (const item of (page?.items || [])) {
      if (item.id === selectedItemId) return { item, groupId: null };
      if (item.kind === "group") {
        for (const child of (item.children || [])) {
          if (child.id === selectedItemId) return { item: child, groupId: item.id };
        }
      }
    }
    return null;
  }, [selectedItemId, pages, activePageIdx]);

  // Clear selection on page change
  useEffect(() => { setSelectedItemId(null); }, [activePageIdx]);

  // Load user's practices once on mount
  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      getUserPracticeById(decoded._id).then(data => {
        if (Array.isArray(data)) setPracName(data);
      }).catch(console.error);
    } catch (e) {
      console.error("Failed to decode token", e);
    }
    getDisciplineCodes().then(groups => {
      const byCode = new Map();
      for (const group of (Array.isArray(groups) ? groups : [])) {
        for (const d of (group?.disciplines || [])) {
          const code = String(d?.disciplineCode || "").trim();
          if (code && !byCode.has(code)) {
            byCode.set(code, { disciplineCode: code, disciplineName: d.disciplineName || code });
          }
        }
      }
      setDisciplineOptions(
        [...byCode.values()].sort((a, b) => a.disciplineName.localeCompare(b.disciplineName)),
      );
    }).catch(console.error);
  }, []);

  // Default a NEW practice template to its practices' disciplines. Stops the
  // moment the user picks disciplines by hand, and never applies to library
  // templates (a SystemAdmin tags those deliberately) or to edits.
  useEffect(() => {
    if (!show || isLibrary || mode === "edit" || disciplinesTouched) return;
    const byCode = new Map();
    for (const prac of pracName) {
      if (!selectedPractices.includes(prac.practiceNumber)) continue;
      const code = String(prac.practiceDisciplineCode || "").trim();
      if (!code || byCode.has(code)) continue;
      const known = disciplineOptions.find(d => d.disciplineCode === code);
      byCode.set(code, {
        disciplineCode: code,
        disciplineName: prac.practiceDisciplineName || known?.disciplineName || code,
      });
    }
    setMeta(m => ({ ...m, templateDisciplines: [...byCode.values()] }));
  }, [show, isLibrary, mode, disciplinesTouched, pracName, selectedPractices, disciplineOptions]);

  const isAllDisciplines = (meta.templateDisciplines || []).length === 0;

  const toggleDiscipline = (option) => {
    setDisciplinesTouched(true);
    setMeta(m => {
      const current = m.templateDisciplines || [];
      const has = current.some(d => d.disciplineCode === option.disciplineCode);
      return {
        ...m,
        templateDisciplines: has
          ? current.filter(d => d.disciplineCode !== option.disciplineCode)
          : [...current, option],
      };
    });
  };

  const disciplineSummary = isAllDisciplines
    ? "All disciplines"
    : meta.templateDisciplines.length === 1
      ? meta.templateDisciplines[0].disciplineName
      : `${meta.templateDisciplines.length} disciplines`;

  // Initialise / reset state every time the modal opens
  useEffect(() => {
    if (!show) return;
    setErrors({});
    setCloseAlert(false);
    setSuccessAlert("");
    setSaving(false);
    setPendingImages({});
    setActivePageIdx(0);
    setSelectedItemId(null);

    if (mode === "edit" && template) {
      setMeta({
        templateTitle:       template.templateTitle       || "",
        templateDescription: template.templateDescription || "",
        templateStatus:      template.templateStatus      || "Active",
        templateInitial:     template.templateInitial     || "initial",
        templateLanguage:    template.templateLanguage    || "en",
        templatePracticeId:  template.templatePracticeId  || [],
        templateDisciplines: Array.isArray(template.templateDisciplines) ? template.templateDisciplines : [],
      });
      setSelectedPractices(template.templatePracticeId || []);
      setDisciplinesTouched(true);
      if (Array.isArray(template.pages) && template.pages.length > 0) {
        setPages(template.pages);
      } else {
        setPages(notesToPages(template.notes));
      }
    } else {
      setMeta({
        templateTitle: "", templateDescription: "",
        templateStatus: "Draft", templateInitial: "initial",
        templateLanguage: "en", templatePracticeId: [],
        templateDisciplines: [],
      });
      setSelectedPractices([]);
      setDisciplinesTouched(false);
      setPages([makePage(1)]);
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Item mutators ───────────────────────────────────────────────────────────

  const updateItem = useCallback((itemId, updates, groupId = null) => {
    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const page = next[activePageIdx];
      if (groupId) {
        const grp = page.items.find(it => it.id === groupId);
        if (!grp) return prev;
        const child = grp.children.find(it => it.id === itemId);
        if (!child) return prev;
        Object.assign(child, updates);
      } else {
        const item = page.items.find(it => it.id === itemId);
        if (!item) return prev;
        Object.assign(item, updates);
      }
      return next;
    });
  }, [activePageIdx]);

  const deleteItem = useCallback((itemId, groupId = null) => {
    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const page = next[activePageIdx];
      if (groupId) {
        const grp = page.items.find(it => it.id === groupId);
        if (!grp) return prev;
        grp.children = grp.children.filter(it => it.id !== itemId);
      } else {
        page.items = page.items.filter(it => it.id !== itemId);
      }
      return next;
    });
    setSelectedItemId(prev => prev === itemId ? null : prev);
  }, [activePageIdx]);

  const addElement = useCallback((type, groupId = null) => {
    const el = makeElement(type);
    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const page = next[activePageIdx];
      if (groupId) {
        const grp = page.items.find(it => it.id === groupId);
        if (!grp) return prev;
        grp.children.push(el);
      } else {
        page.items.push(el);
      }
      return next;
    });
    setSelectedItemId(el.id);
  }, [activePageIdx]);

  const addGroup = useCallback(() => {
    const grp = makeGroup();
    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next[activePageIdx].items.push(grp);
      return next;
    });
    setSelectedItemId(grp.id);
  }, [activePageIdx]);

  const addPage = () => {
    const newPage = makePage(pages.length + 1);
    setPages(prev => [...prev, newPage]);
    setActivePageIdx(pages.length);
  };

  const deletePage = (idx) => {
    if (pages.length <= 1) return;
    setPages(prev => prev.filter((_, i) => i !== idx));
    setActivePageIdx(prev => (prev >= idx && prev > 0 ? prev - 1 : prev));
  };

  const renamePage = (idx, newTitle) => {
    const clean = (newTitle || "").trim().slice(0, 80) || `Page ${idx + 1}`;
    setPages(prev => prev.map((p, i) => (i === idx ? { ...p, pageTitle: clean } : p)));
  };

  // ── Move Up / Down ──────────────────────────────────────────────────────────

  // ── Drag and drop ───────────────────────────────────────────────────────────

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    setPages(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const page = next[activePageIdx];

      const srcIsPage  = source.droppableId      === `page-${page.pageId}`;
      const dstIsPage  = destination.droppableId === `page-${page.pageId}`;
      const srcGroupId = source.droppableId.startsWith("group-")
        ? source.droppableId.replace("group-", "") : null;
      const dstGroupId = destination.droppableId.startsWith("group-")
        ? destination.droppableId.replace("group-", "") : null;

      let movingItem;
      if (srcIsPage) {
        [movingItem] = page.items.splice(source.index, 1);
      } else if (srcGroupId) {
        const grp = page.items.find(it => it.id === srcGroupId);
        if (!grp) return prev;
        [movingItem] = grp.children.splice(source.index, 1);
      } else {
        return prev;
      }

      if (dstIsPage) {
        page.items.splice(destination.index, 0, movingItem);
      } else if (dstGroupId) {
        if (movingItem.kind === "group") {
          if (srcIsPage) page.items.splice(source.index, 0, movingItem);
          return next;
        }
        const destGrp = page.items.find(it => it.id === dstGroupId);
        if (!destGrp) return prev;
        destGrp.children.splice(destination.index, 0, movingItem);
      } else {
        return prev;
      }

      return next;
    });
  };

  // ── Image handling ──────────────────────────────────────────────────────────

  const handlePendingImage = (itemId, file) => {
    const preview = URL.createObjectURL(file);
    setPendingImages(prev => ({ ...prev, [itemId]: { file, preview } }));
  };

  // ── Save helpers ─────────────────────────────────────────────────────────────

  const validateMeta = () => {
    const newErrors = {};
    if (!meta.templateTitle.trim())       newErrors.templateTitle       = "Template Title is required.";
    if (!meta.templateDescription.trim()) newErrors.templateDescription = "Description is required.";
    // A library template belongs to no practice, so there is nothing to pick.
    if (!isLibrary && selectedPractices.length === 0)
      newErrors.templatePracticeId = "At least one practice is required.";
    return newErrors;
  };

  const uploadPendingImages = async (pagesSnapshot) => {
    const updatedPages = JSON.parse(JSON.stringify(pagesSnapshot));
    const uploadForItems = async (items) => {
      for (const item of items) {
        if (item.kind === "group") {
          await uploadForItems(item.children);
        } else if (item.type === "image" && pendingImages[item.id]) {
          const { file } = pendingImages[item.id];
          const formData = new FormData();
          formData.append("image", file);
          formData.append("x-image-caption", "Template Image");
          const res = await uploadImage(formData);
          item.imageUrl = res.imageUrl;
        }
      }
    };
    for (const page of updatedPages) {
      await uploadForItems(page.items);
    }
    return updatedPages;
  };

  // ── Save Draft ────────────────────────────────────────────────────────────────

  const handleSaveDraft = async () => {
    const newErrors = validateMeta();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSaving("draft");
    try {
      const updatedPages = await uploadPendingImages(pages);
      const payload = { ...meta, templatePracticeId: selectedPractices, pages: updatedPages };
      if (isLibrary) delete payload.templatePracticeId;

      if (isLibrary) {
        if (mode === "add") await createLibraryTemplate(payload);
        else                await updateLibraryTemplate(template._id, payload);
      } else if (mode === "add") {
        await createTemplate(payload);
      } else {
        await updateTemplate(template._id, payload);
      }

      setSuccessAlert("Template saved as draft.");
      setPendingImages({});
      const returnedTemplate = mode === "edit"
        ? { ...template, ...payload, pages: updatedPages }
        : payload;

      setTimeout(() => {
        setSuccessAlert("");
        setSaving(false);
        onSaved(returnedTemplate);
      }, 1500);
    } catch (err) {
      setSaving(false);
      setErrors({ _general: err.message || "Failed to save template." });
    }
  };

  // ── Publish ───────────────────────────────────────────────────────────────────

  const handlePublish = async () => {
    const newErrors = validateMeta();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSaving("publish");
    try {
      const updatedPages = await uploadPendingImages(pages);
      // Publish promotes Draft → Active, but respects an explicit "Inactive"
      // choice so the user can deactivate a template via the editor.
      const resolvedStatus = meta.templateStatus === "Inactive" ? "Inactive" : "Active";
      const payload = {
        ...meta,
        templatePracticeId: selectedPractices,
        pages: updatedPages,
        templateStatus: resolvedStatus,
      };

      let templateId = template?._id;

      // Library templates carry no practices, and publishing one is simply
      // making it Active — the version-snapshot route refuses system templates,
      // because version history belongs to the practice copy that gets filled
      // in, not to the library document practices adopt from.
      if (isLibrary) {
        delete payload.templatePracticeId;
        if (mode === "add") {
          const result = await createLibraryTemplate(payload);
          templateId = result.data?.insertedId?.toString() || result?.insertedId?.toString();
          if (!templateId) throw new Error("Failed to retrieve template ID after creation.");
        } else {
          await updateLibraryTemplate(template._id, payload);
        }

        setMeta(m => ({ ...m, templateStatus: resolvedStatus }));
        setSuccessAlert("Library template published successfully!");
        setPendingImages({});
        const returned = mode === "edit"
          ? { ...template, ...payload, pages: updatedPages }
          : { ...payload, _id: templateId };
        setTimeout(() => {
          setSuccessAlert("");
          setSaving(false);
          onSaved(returned);
        }, 1500);
        return;
      }

      if (mode === "add") {
        const result = await createTemplate(payload);
        // MongoDB driver v6: insertedId is an ObjectId — must call .toString()
        templateId = result.data?.insertedId?.toString() || result?.insertedId?.toString();
        if (!templateId) throw new Error("Failed to retrieve template ID after creation.");
      } else {
        await updateTemplate(template._id, payload);
      }

      await publishTemplate(templateId);

      setMeta(m => ({ ...m, templateStatus: resolvedStatus }));
      setSuccessAlert("Template published successfully!");
      setPendingImages({});
      const returnedTemplate = mode === "edit"
        ? { ...template, ...payload, pages: updatedPages }
        : { ...payload, _id: templateId };

      setTimeout(() => {
        setSuccessAlert("");
        setSaving(false);
        onSaved(returnedTemplate);
      }, 1500);
    } catch (err) {
      setSaving(false);
      setErrors({ _general: err.message || "Failed to publish template." });
    }
  };

  // ── Close guard ─────────────────────────────────────────────────────────────

  const handleCloseGuard = () => {
    const isDirty = meta.templateTitle.trim() ||
      selectedPractices.length > 0 ||
      pages.some(p => p.items.length > 0);
    if (isDirty) {
      setCloseAlert(true);
    } else {
      onHide();
    }
  };

  const confirmClose = () => {
    setCloseAlert(false);
    onHide();
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const activePage = pages[activePageIdx] || pages[0] || { pageId: "", items: [] };

  /**
   * Width of a canvas item as a percentage of the row.
   *
   * The canvas mirrors what NoteRenderer produces: items flow left-to-right and
   * a half-width field occupies half the row, so the builder shows the layout
   * rather than a stack of full-width cards that only reveals its shape on
   * preview. Dividers span the row there, so they span it here too.
   */
  const colWidth = (item) => {
    const span = item.type === "divider" ? 12 : (item.colSpan || 12);
    return `${(span / 12) * 100}%`;
  };

  /** Divider — no card box, just the line */
  const renderDivider = (item, dragHandleProps) => (
    <div
      {...dragHandleProps}
      className="tb-divider-row"
      onClick={() => setSelectedItemId(item.id)}
    >
      <hr className="flex-grow-1 my-0" />
    </div>
  );

  /** Title/Header — no card box, just editable heading */
  const renderTitle = (item, dragHandleProps, groupId = null) => (
    <div {...dragHandleProps} className="tb-title-row">
      <input
        className="tb-title-input"
        value={item.label || ""}
        onChange={e => updateItem(item.id, { label: e.target.value }, groupId)}
        placeholder="Enter heading text…"
        onClick={e => { e.stopPropagation(); setSelectedItemId(item.id); }}
      />
    </div>
  );

  /** Static text row — flat, no card box */
  const renderStaticText = (item, dragHandleProps) => (
    <div
      {...dragHandleProps}
      className="tb-title-row"
      style={{ border: "1px dashed #adb5bd" }}
      onClick={() => setSelectedItemId(item.id)}
    >
      <span style={{ fontStyle: "italic", color: "#6c757d", flexGrow: 1, fontSize: "0.9rem" }}>
        {item.label || "Static text…"}
      </span>
    </div>
  );

  /** Spacer row — flat, shows height indicator */
  const renderSpacer = (item, dragHandleProps) => (
    <div
      {...dragHandleProps}
      className="tb-divider-row"
      onClick={() => setSelectedItemId(item.id)}
    >
      <div
        style={{ height: Math.max(item.height || 24, 12), flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <span style={{ fontSize: "0.75rem", color: "#adb5bd" }}>{item.height || 24}px</span>
      </div>
    </div>
  );

  /**
   * The inline label editor every field card carries above its preview.
   *
   * Every interactive type needs one: the label is what the practitioner reads
   * next to the field in the note, and a card without it shows a control with
   * nothing identifying it.
   */
  const fieldLabelInput = (item, groupId, placeholder = "Field label…") => (
    <input
      className="tb-field-label-input"
      value={item.label || ""}
      onChange={e => updateItem(item.id, { label: e.target.value }, groupId)}
      placeholder={placeholder}
      onClick={e => e.stopPropagation()}
    />
  );

  /** Regular element card — always expanded, form-like */
  const renderElementCard = (item, dragHandleProps, groupId = null) => {
    if (item.type === "divider")     return renderDivider(item, dragHandleProps, groupId);
    if (item.type === "title")       return renderTitle(item, dragHandleProps, groupId);
    if (item.type === "static_text") return renderStaticText(item, dragHandleProps, groupId);
    if (item.type === "spacer")      return renderSpacer(item, dragHandleProps, groupId);

    const pending = pendingImages[item.id];
    const isSelected = selectedItemId === item.id;

    // No header row: the type label, width toggle and delete button all live in
    // the properties panel on the right, and the whole card is the drag handle
    // (react-beautiful-dnd refuses to start a drag from an input or textarea, so
    // the fields inside stay editable). What is left on the canvas is the field
    // as the practitioner will see it.
    return (
      <div
        {...dragHandleProps}
        className={`tb-item-card${isSelected ? " selected" : ""}`}
        onClick={() => setSelectedItemId(item.id)}
      >
        {/* Card body — form-like layout */}
        <div className="tb-card-body">

          {/* ── TEXT ── */}
          {item.type === "text" && (
            <Form.Group>
              {fieldLabelInput(item, groupId)}
              <Form.Control
                type="text"
                size="sm"
                value={item.defaultValue || ""}
                onChange={e => updateItem(item.id, { defaultValue: e.target.value }, groupId)}
                placeholder="Default value (optional)"
                onClick={e => e.stopPropagation()}
              />
            </Form.Group>
          )}

          {/* ── MEMO ── */}
          {item.type === "memo" && (
            <Form.Group>
              {fieldLabelInput(item, groupId)}
              {/* Same height expression NoteRenderer uses for a memo, so the
                  canvas box is the size the practitioner will actually type
                  into. rows alone is not enough — the note pins the height with
                  this minHeight, and the compact 1.2 line-height in custom.scss
                  makes four rows here shorter than four lines there. */}
              <Form.Control
                as="textarea"
                rows={4}
                style={{ minHeight: "calc(4 * 1.5rem + 0.75rem)" }}
                size="sm"
                value={item.defaultValue || ""}
                onChange={e => updateItem(item.id, { defaultValue: e.target.value }, groupId)}
                placeholder="Default text (optional)"
                onClick={e => e.stopPropagation()}
              />
            </Form.Group>
          )}

          {/* ── RICH MEMO ── */}
          {/* The same Quill editor the practitioner gets at fill time, sized
              by the element's "rows" setting. react-beautiful-dnd never starts
              a drag from a contenteditable surface, so the card stays the drag
              handle while the editor stays editable. */}
          {item.type === "rich_memo" && (
            <Form.Group>
              {fieldLabelInput(item, groupId)}
              <div
                className="rich-memo-field"
                style={{ "--rm-rows": Number(item.rows) > 0 ? Number(item.rows) : 4 }}
                onClick={e => e.stopPropagation()}
              >
                <ReactQuill
                  value={sanitizeRichMemoHtml(item.defaultValue || "")}
                  placeholder="Default rich text (optional)"
                  modules={RICH_MEMO_QUILL_MODULES}
                  formats={RICH_MEMO_QUILL_FORMATS}
                  onChange={html => updateItem(item.id, { defaultValue: normalizeRichMemoHtml(html) }, groupId)}
                />
              </div>
            </Form.Group>
          )}

          {/* ── CHECKBOX GROUP ── */}
          {item.type === "checkbox" && (
            <div onClick={e => e.stopPropagation()}>
              {fieldLabelInput(item, groupId, "Question label…")}
              <div className="mt-1">
                {(item.options || []).map((opt, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 mb-1">
                    <Form.Check
                      type="checkbox"
                      checked={(item.defaultValue || []).includes(opt)}
                      onChange={e => {
                        const dv = item.defaultValue || [];
                        updateItem(item.id, {
                          defaultValue: e.target.checked
                            ? [...dv, opt]
                            : dv.filter(v => v !== opt),
                        }, groupId);
                      }}
                      className="flex-shrink-0 mb-0"
                    />
                    <Form.Control
                      size="sm"
                      value={opt}
                      onChange={e => {
                        const opts = [...(item.options || [])];
                        const old = opts[i];
                        opts[i] = e.target.value;
                        const dv = (item.defaultValue || []).map(v => v === old ? e.target.value : v);
                        updateItem(item.id, { options: opts, defaultValue: dv }, groupId);
                      }}
                      placeholder={`Option ${i + 1}`}
                    />
                    <button
                      className="btn btn-link p-0 text-danger flex-shrink-0"
                      style={{ fontSize: "0.8rem" }}
                      title="Remove option"
                      onClick={() => {
                        const opts = (item.options || []).filter((_, j) => j !== i);
                        const dv = (item.defaultValue || []).filter(v => v !== opt);
                        updateItem(item.id, { options: opts, defaultValue: dv }, groupId);
                      }}
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── IMAGE ── */}
          {item.type === "image" && (
            <div onClick={e => e.stopPropagation()}>
              {fieldLabelInput(item, groupId, "Image label…")}
              <Form.Control
                type="file"
                size="sm"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                className="mb-2"
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) handlePendingImage(item.id, file);
                }}
              />
              {(pending?.preview || item.imageUrl) && (
                <div>
                  <img
                    src={pending?.preview || item.imageUrl}
                    alt="Preview"
                    className="rounded mb-2 d-block"
                    style={{ width: `${item.imageWidth || 100}%`, maxWidth: "100%" }}
                  />
                  <div className="d-flex align-items-center gap-2">
                    <Form.Label className="small text-muted mb-0 flex-shrink-0">
                      Width: {item.imageWidth || 100}%
                    </Form.Label>
                    <Form.Range
                      min={10} max={100} step={5}
                      value={item.imageWidth || 100}
                      onChange={e => updateItem(item.id, { imageWidth: Number(e.target.value) }, groupId)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── PHOTO UPLOAD ── */}
          {item.type === "photo_upload" && (
            <div onClick={e => e.stopPropagation()}>
              {fieldLabelInput(item, groupId)}
              <div
                className="mt-1"
                style={{
                  border: "2px dashed #adb5bd",
                  borderRadius: 6,
                  padding: "1rem",
                  textAlign: "center",
                  background: "#f8f9fa",
                  fontSize: "0.8rem",
                  color: "#6c757d",
                }}
              >
                <i className="fa-light fa-image-plus me-1" /> User uploads or captures photo here
              </div>
            </div>
          )}

          {/* ── RADIO ── */}
          {/* Same shape as the checkbox card: a label and the options laid out
              as they will appear in the note. The only difference is that a
              radio holds ONE default (a string, not an array), so selecting an
              option clears whichever was selected before. */}
          {item.type === "radio" && (
            <div onClick={e => e.stopPropagation()}>
              {fieldLabelInput(item, groupId, "Question label…")}
              <div className="mt-1">
                {(item.options || []).map((opt, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 mb-1">
                    <Form.Check
                      type="radio"
                      name={`tb-radio-${item.id}`}
                      checked={item.defaultValue === opt}
                      // A radio never fires onChange when it is already the
                      // selected one, so clearing the default is handled on
                      // click — press the selected option again to choose none.
                      onChange={() => updateItem(item.id, { defaultValue: opt }, groupId)}
                      onClick={() => {
                        if (item.defaultValue === opt) {
                          updateItem(item.id, { defaultValue: null }, groupId);
                        }
                      }}
                      className="flex-shrink-0 mb-0"
                    />
                    <Form.Control
                      size="sm"
                      value={opt}
                      onChange={e => {
                        const opts = [...(item.options || [])];
                        const old = opts[i];
                        opts[i] = e.target.value;
                        const dv = item.defaultValue === old ? e.target.value : item.defaultValue;
                        updateItem(item.id, { options: opts, defaultValue: dv }, groupId);
                      }}
                      placeholder={`Option ${i + 1}`}
                    />
                    <button
                      className="btn btn-link p-0 text-danger flex-shrink-0"
                      style={{ fontSize: "0.8rem" }}
                      title="Remove option"
                      onClick={() => {
                        const opts = (item.options || []).filter((_, j) => j !== i);
                        const dv = item.defaultValue === opt ? null : item.defaultValue;
                        updateItem(item.id, { options: opts, defaultValue: dv }, groupId);
                      }}
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── YES / NO ── */}
          {item.type === "yes_no" && (
            <div onClick={e => e.stopPropagation()}>
              {fieldLabelInput(item, groupId, "Question label…")}
              <div className="mt-1">
                {["Yes", "No"].map(opt => (
                  <Form.Check
                    key={opt}
                    type="radio"
                    inline
                    disabled
                    readOnly
                    label={opt}
                    name={`tb-yesno-${item.id}`}
                    // Case-insensitive: templates saved before the properties
                    // panel was corrected hold "yes" / "no" in lower case.
                    checked={String(item.defaultValue || "").toLowerCase() === opt.toLowerCase()}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── NUMERIC ── */}
          {item.type === "numeric" && (
            <div onClick={e => e.stopPropagation()}>
              {fieldLabelInput(item, groupId)}
              <div className="d-flex align-items-center gap-2 mt-1">
                <Form.Control
                  type="number"
                  size="sm"
                  disabled
                  placeholder={item.placeholder || "0"}
                  style={{ maxWidth: 120 }}
                />
                {item.unit && <span className="small text-muted">{item.unit}</span>}
              </div>
            </div>
          )}

          {/* ── PAIN SCALE ── */}
          {item.type === "pain_scale" && (
            <div onClick={e => e.stopPropagation()}>
              {fieldLabelInput(item, groupId)}
              <div className="d-flex justify-content-between text-muted small mt-1 mb-1">
                <span>{item.minLabel ? `0 — ${item.minLabel}` : "0"}</span>
                <span>{item.maxLabel ? `10 — ${item.maxLabel}` : "10"}</span>
              </div>
              <Form.Range min={0} max={10} step={1} value={item.defaultValue ?? 5} disabled />
              <div className="text-center fw-bold small">{item.defaultValue ?? 5}</div>
            </div>
          )}

          {/* ── DROPDOWN ── */}
          {/* A real select, so expanding it on the canvas shows exactly the
              options a practitioner will see, with the default preselected.
              Adding and renaming options happens in the properties panel — a
              native select can't host editable rows the way the checkbox card
              does, so there is no second place to do it from here. */}
          {item.type === "dropdown" && (
            <div onClick={e => e.stopPropagation()}>
              {fieldLabelInput(item, groupId, "Question label…")}
              <Form.Select
                size="sm"
                className="mt-1"
                value={item.defaultValue ?? ""}
                onChange={e => updateItem(item.id, { defaultValue: e.target.value || null }, groupId)}
              >
                <option value="">{item.placeholder || "Select…"}</option>
                {(item.options || []).map((opt, i) => (
                  <option key={i} value={opt}>{opt}</option>
                ))}
              </Form.Select>
            </div>
          )}

          {/* ── DATE ── */}
          {item.type === "date" && (
            <div onClick={e => e.stopPropagation()}>
              {fieldLabelInput(item, groupId)}
              <Form.Control type="date" size="sm" className="mt-1" disabled />
            </div>
          )}

        </div>
      </div>
    );
  };

  /** Group card with inner droppable */
  const renderGroupCard = (item, dragHandleProps) => {
    const isSelected = selectedItemId === item.id;
    return (
      <div
        className={`tb-group-card${isSelected ? " selected" : ""}`}
        onClick={() => setSelectedItemId(item.id)}
      >
        {/* A group keeps a title strip as its drag handle. The whole card can't
            be one — it contains its own droppable, and dragging a child would
            fight with dragging the group. */}
        <div {...dragHandleProps} className="tb-group-header">
          <small className="fw-bold text-secondary flex-shrink-0">GROUP</small>
          <span style={{ flexGrow: 1, color: "#6c757d", fontSize: "0.875rem", fontStyle: item.groupTitle ? "normal" : "italic" }}>
            {item.groupTitle || "Untitled group"}
          </span>
        </div>

        <Droppable droppableId={`group-${item.id}`} direction="vertical">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`tb-group-drop${snapshot.isDraggingOver ? " dragging-over" : ""}`}
              onClick={e => e.stopPropagation()}
            >
              {item.children.map((child, cIdx) => (
                <Draggable key={child.id} draggableId={child.id} index={cIdx}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="tb-canvas-item"
                      style={{
                        ...provided.draggableProps.style,
                        // While dragging, react-beautiful-dnd sets an explicit
                        // width from the source box — overriding it would make
                        // the dragged card jump to a different size mid-drag.
                        ...(snapshot.isDragging ? {} : { width: colWidth(child) }),
                      }}
                    >
                      {renderElementCard(child, provided.dragHandleProps, item.id)}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {item.children.length === 0 && !snapshot.isDraggingOver && (
                <p className="text-muted small text-center my-1 mb-0 w-100">
                  Drag elements here or click below.
                </p>
              )}
            </div>
          )}
        </Droppable>

        <AddToGroupPicker onAdd={type => addElement(type, item.id)} />
      </div>
    );
  };

  // ── PropertiesPanel ─────────────────────────────────────────────────────────

  function renderPropertiesPanel({ selected, updateItem, deleteItem, handlePendingImage, pendingImages }) {
    if (!selected) {
      return <p className="tb-props-panel-empty">Select a field on the canvas to edit its properties.</p>;
    }
    const { item, groupId } = selected;

    // ── Group properties ────────────────────────────────────────────────────
    if (item.kind === "group") {
      return (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <small className="fw-bold text-secondary" style={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>Group / Section</small>
            <button className="btn btn-link p-0 text-danger" style={{ fontSize: "0.8rem" }}
              onClick={() => deleteItem(item.id)} title="Delete group">
              <i className="fa-regular fa-trash-can" />
            </button>
          </div>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Section Title</Form.Label>
            <Form.Control size="sm" value={item.groupTitle || ""}
              onChange={e => updateItem(item.id, { groupTitle: e.target.value })}
              placeholder="Group title…" />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Group Width</Form.Label>
            <div className="d-flex gap-1">
              {[{ label: "Full", span: 12 }, { label: "½", span: 6 }, { label: "⅓", span: 4 }].map(({ label, span }) => (
                <button key={span}
                  className={`btn btn-sm ${(item.colSpan || 12) === span ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => updateItem(item.id, { colSpan: span })}
                >{label}</button>
              ))}
            </div>
            <small className="text-muted d-block mt-1" style={{ fontSize: "0.72rem" }}>How wide the group is — set Full/½/⅓ to place groups side by side.</small>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Children Column Preset</Form.Label>
            <div className="d-flex gap-1">
              {[1, 2, 3].map(cols => {
                const span = cols === 1 ? 12 : cols === 2 ? 6 : 4;
                const active = (item.children || []).every(c => c.colSpan === span);
                return (
                  <button key={cols}
                    className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => {
                      const updatedChildren = (item.children || []).map(c => ({ ...c, colSpan: span }));
                      updateItem(item.id, { children: updatedChildren });
                    }}
                  >{cols}-col</button>
                );
              })}
            </div>
            <small className="text-muted d-block mt-1" style={{ fontSize: "0.72rem" }}>Sets all children to equal width. Individual widths can still be adjusted.</small>
          </Form.Group>
        </div>
      );
    }

    // ── Element properties ──────────────────────────────────────────────────
    const pending = pendingImages[item.id];
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <small className="fw-bold text-secondary" style={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {FIELD_TYPE_LABELS[item.type] || item.type}
          </small>
          <button className="btn btn-link p-0 text-danger" style={{ fontSize: "0.8rem" }}
            onClick={() => deleteItem(item.id, groupId)} title="Delete field">
            <i className="fa-regular fa-trash-can" />
          </button>
        </div>

        {/* Label — all types except divider */}
        {item.type !== "divider" && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">{item.type === "static_text" ? "Text Content" : "Label"}</Form.Label>
            <Form.Control size="sm" value={item.label || ""}
              onChange={e => updateItem(item.id, { label: e.target.value }, groupId)}
              placeholder={item.type === "static_text" ? "Static text content…" : "Field label…"} />
          </Form.Group>
        )}

        {/* Required — interactive types only */}
        {INTERACTIVE_TYPES.includes(item.type) && (
          <Form.Check type="checkbox" label="Required field" className="mb-2 small"
            checked={!!item.required}
            onChange={e => updateItem(item.id, { required: e.target.checked }, groupId)} />
        )}

        {/* Placeholder: text, memo, rich memo, numeric, date */}
        {["text", "memo", "rich_memo", "numeric", "date"].includes(item.type) && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Placeholder</Form.Label>
            <Form.Control size="sm" value={item.placeholder || ""}
              onChange={e => updateItem(item.id, { placeholder: e.target.value }, groupId)}
              placeholder="Helper text shown to practitioner…" />
          </Form.Group>
        )}

        {/* Rows open by default (rich memo). Controls how tall the editor
            opens when the note is filled in; it still grows with content. */}
        {item.type === "rich_memo" && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Rows open by default</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              min={2}
              max={40}
              value={Number(item.rows) > 0 ? Number(item.rows) : 4}
              onChange={e => {
                const rows = Math.max(2, Math.min(40, Number(e.target.value) || 4));
                updateItem(item.id, { rows }, groupId);
              }}
              style={{ maxWidth: 100 }}
            />
            <small className="text-muted d-block mt-1" style={{ fontSize: "0.72rem" }}>
              How many rows the box opens at. It grows as content is added.
            </small>
          </Form.Group>
        )}

        {/* Column width — all except divider and group */}
        {item.type !== "divider" && item.kind !== "group" && (
          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Width</Form.Label>
            <div className="tb-col-toggle">
              {COL_OPTIONS.map(({ span, label }) => (
                <button key={span}
                  className={`tb-col-btn${item.colSpan === span ? " active" : ""}`}
                  onClick={() => updateItem(item.id, { colSpan: span }, groupId)}
                >{label}</button>
              ))}
            </div>
          </Form.Group>
        )}

        {/* Options list — checkbox, radio, dropdown */}
        {["checkbox", "radio", "dropdown"].includes(item.type) && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Options</Form.Label>
            {(item.options || []).map((opt, i) => (
              <div key={i} className="d-flex align-items-center gap-1 mb-1">
                <Form.Control size="sm" value={opt}
                  onChange={e => {
                    const opts = [...(item.options || [])];
                    const old = opts[i];
                    opts[i] = e.target.value;
                    const dv = item.type === "checkbox"
                      ? (item.defaultValue || []).map(v => v === old ? e.target.value : v)
                      : item.defaultValue === old ? e.target.value : item.defaultValue;
                    updateItem(item.id, { options: opts, defaultValue: dv }, groupId);
                  }}
                  placeholder={`Option ${i + 1}`} />
                <button className="btn btn-link p-0 text-danger flex-shrink-0" style={{ fontSize: "0.75rem" }}
                  onClick={() => {
                    const opts = (item.options || []).filter((_, j) => j !== i);
                    const dv = item.type === "checkbox"
                      ? (item.defaultValue || []).filter(v => v !== opt)
                      : item.defaultValue === opt ? null : item.defaultValue;
                    updateItem(item.id, { options: opts, defaultValue: dv }, groupId);
                  }}><i className="fas fa-times" /></button>
              </div>
            ))}
            <Button variant="outline-secondary" size="sm" className="mt-1 w-100"
              onClick={() => {
                const opts = [...(item.options || []), `Option ${(item.options || []).length + 1}`];
                updateItem(item.id, { options: opts }, groupId);
              }}>+ Add option</Button>
          </Form.Group>
        )}

        {/* Scale end labels — what the two ends of the slider mean. Left blank
            the scale is just 0–10, which is what makes it usable for something
            other than pain. */}
        {item.type === "pain_scale" && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Scale ends</Form.Label>
            <div className="d-flex align-items-center gap-1">
              <Form.Control size="sm" value={item.minLabel || ""}
                onChange={e => updateItem(item.id, { minLabel: e.target.value }, groupId)}
                placeholder="0 (e.g. No pain)" />
              <Form.Control size="sm" value={item.maxLabel || ""}
                onChange={e => updateItem(item.id, { maxLabel: e.target.value }, groupId)}
                placeholder="10 (e.g. Worst pain)" />
            </div>
          </Form.Group>
        )}

        {/* Unit — numeric */}
        {item.type === "numeric" && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Unit</Form.Label>
            <Form.Control size="sm" value={item.unit || ""}
              onChange={e => updateItem(item.id, { unit: e.target.value }, groupId)}
              placeholder="e.g. kg, cm, degrees…" />
          </Form.Group>
        )}

        {/* Spacer height */}
        {item.type === "spacer" && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Height (px)</Form.Label>
            <Form.Select size="sm" value={item.height || 24}
              onChange={e => updateItem(item.id, { height: Number(e.target.value) }, groupId)}>
              <option value={12}>Small (12px)</option>
              <option value={24}>Medium (24px)</option>
              <option value={48}>Large (48px)</option>
              <option value={72}>Extra Large (72px)</option>
            </Form.Select>
          </Form.Group>
        )}

        {/* Yes/No default */}
        {item.type === "yes_no" && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Default</Form.Label>
            <Form.Select size="sm" value={item.defaultValue ?? ""}
              onChange={e => updateItem(item.id, { defaultValue: e.target.value || null }, groupId)}>
              <option value="">None</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </Form.Select>
          </Form.Group>
        )}

        {/* Pain scale default */}
        {item.type === "pain_scale" && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Default value: {item.defaultValue ?? 0}</Form.Label>
            <Form.Range min={0} max={10} step={1} value={item.defaultValue ?? 0}
              onChange={e => updateItem(item.id, { defaultValue: Number(e.target.value) }, groupId)} />
          </Form.Group>
        )}

        {/* Image upload */}
        {item.type === "image" && (
          <div>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Image</Form.Label>
              <Form.Control type="file" size="sm"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                onChange={e => { const f = e.target.files[0]; if (f) handlePendingImage(item.id, f); }} />
            </Form.Group>
            {(pending?.preview || item.imageUrl) && (
              <div>
                <img src={pending?.preview || item.imageUrl} alt="Preview"
                  className="rounded mb-2 d-block"
                  style={{ width: `${item.imageWidth || 100}%`, maxWidth: "100%" }} />
                <Form.Group>
                  <Form.Label className="small mb-1">Width: {item.imageWidth || 100}%</Form.Label>
                  <Form.Range min={10} max={100} step={5} value={item.imageWidth || 100}
                    onChange={e => updateItem(item.id, { imageWidth: Number(e.target.value) }, groupId)} />
                </Form.Group>
              </div>
            )}
          </div>
        )}

        {/* ── SyncTo AI ──────────────────────────────────────────────────────
            How a dictated recording fills this field. Everything here is
            authoring metadata: none of it is shown to the practitioner filling
            the note in, and none of it reaches the printed PDF. */}
        {AI_TYPES.includes(item.type) && (
          <div className="tb-ai-section">
            <div className="d-flex align-items-center mb-2">
              <small className="fw-bold text-secondary" style={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
                SyncTo AI
              </small>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Controls how a dictated recording fills this field. Nothing here is shown to the practitioner.</Tooltip>}
              >
                <i className="fa-solid fa-circle-info ms-2 text-primary" style={{ fontSize: "0.75rem" }} />
              </OverlayTrigger>
            </div>

            {/* Legacy markers still sitting in the default text. Opt-in per
                field — nothing is converted automatically, and the backend
                keeps honouring the markers either way. */}
            {hasLegacyMarkers(item.defaultValue) && (
              <Alert variant="info" className="py-2 px-2 small mb-2">
                This field's default text still uses the old markers
                (<code>{"{use default}"}</code> / <code>{"{from = to}"}</code>).
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="mt-2 d-block"
                  onClick={() => updateItem(item.id, markersToAiFields(item.defaultValue), groupId)}
                >
                  Move to AI fields
                </Button>
              </Alert>
            )}

            <Form.Check
              type="checkbox"
              className="mb-2 small"
              id={`ai-exclude-${item.id}`}
              label="Never fill this field from a recording"
              checked={!!item.aiExclude}
              onChange={e => updateItem(item.id, { aiExclude: e.target.checked ? true : undefined }, groupId)}
            />

            {!item.aiExclude && (
              <>
                {/* rich_memo formatting: how the AI structures its rich text
                    output for this field. Mirrors rule 11 of the routing
                    prompt in backend/src/syncToService.js. The keep-default
                    checkbox shares the group: it is the same kind of choice
                    (what the routed output looks like), and for text / memo
                    fields it is the group's only entry. */}
                {(item.type === "rich_memo" || AI_KEEP_TYPES.includes(item.type)) && (
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Formatting</Form.Label>
                    {item.type === "rich_memo" && (
                      <>
                        <Form.Check
                          type="checkbox"
                          className="small"
                          id={`ai-headings-${item.id}`}
                          label="Expand content into headings and sub-headings"
                          checked={!!item.aiHeadings}
                          onChange={e => updateItem(item.id, { aiHeadings: e.target.checked ? true : undefined }, groupId)}
                        />
                        <Form.Check
                          type="checkbox"
                          className="small"
                          id={`ai-bullets-${item.id}`}
                          label="Use bullets and sub-bullets"
                          checked={!!item.aiBullets}
                          onChange={e => updateItem(item.id, { aiBullets: e.target.checked ? true : undefined }, groupId)}
                        />
                        <Form.Check
                          type="checkbox"
                          className="small"
                          id={`ai-bold-headings-${item.id}`}
                          label="Make headings and sub-headings bold"
                          checked={!!item.aiBoldHeadings}
                          onChange={e => updateItem(item.id, { aiBoldHeadings: e.target.checked ? true : undefined }, groupId)}
                        />
                      </>
                    )}
                    {/* Only prose defaults can act as the floor of the AI's
                        output. A checkbox default is a list of options, not
                        text. Unchecking CLEARS aiMode rather than writing
                        "guidance": guidance is the default, and an element
                        carrying any ai* field stops using its legacy
                        defaultValue markers, so writing the mode explicitly
                        would silently drop a legacy field's AI guidance the
                        moment someone toggled this without typing anything. */}
                    {AI_KEEP_TYPES.includes(item.type) && (
                      <Form.Check
                        type="checkbox"
                        className="small"
                        id={`ai-keep-default-${item.id}`}
                        label="Keep the default text when a recording fills this field"
                        checked={item.aiMode === "keep"}
                        onChange={e => updateItem(item.id, { aiMode: e.target.checked ? "keep" : undefined }, groupId)}
                      />
                    )}
                    <small className="text-muted d-block mt-1" style={{ fontSize: "0.72rem" }}>
                      {item.type === "rich_memo" && (
                        <>
                          Headings and sub-headings are created from the recording's
                          content and its natural progression. They do not need to
                          exist in the template.{" "}
                        </>
                      )}
                      {AI_KEEP_TYPES.includes(item.type) && (
                        item.aiMode === "keep"
                          ? "The default text stays in the note and dictated content is layered on top."
                          : "The default text is ignored when a recording fills this field."
                      )}
                    </small>
                  </Form.Group>
                )}

                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Instructions for SyncTo</Form.Label>
                  <Form.Control
                    as="textarea"
                    // Roomy by default: instructions are usually a list of
                    // structures or section headings to listen for, not a
                    // one-liner. Still drag-resizable from the corner.
                    rows={8}
                    size="sm"
                    style={{ minHeight: 150 }}
                    value={item.aiInstructions || ""}
                    onChange={e => updateItem(item.id, { aiInstructions: e.target.value }, groupId)}
                    placeholder={item.type === "checkbox"
                      ? "e.g. Tick when the patient reports night pain."
                      : "What should SyncTo listen for, and how should it organise it? e.g. Organise the notes under these headings: 1. Objective, 2. Subjective, 3. Plan."}
                  />
                  <small className="text-muted d-block mt-1" style={{ fontSize: "0.72rem" }}>
                    Tells SyncTo what to listen for and how to organise it, for
                    example under named headings. Instructions are followed,
                    never copied into the note.
                  </small>
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Term replacements</Form.Label>
                  {(item.aiReplacements || []).map((pair, idx) => (
                    <div key={idx} className="d-flex align-items-center gap-1 mb-1">
                      <Form.Control
                        size="sm"
                        value={pair.from || ""}
                        placeholder="left hand"
                        onChange={e => {
                          const next = (item.aiReplacements || []).map((r, i) =>
                            i === idx ? { ...r, from: e.target.value } : r);
                          updateItem(item.id, { aiReplacements: next }, groupId);
                        }}
                      />
                      <i className="fa-solid fa-arrow-right text-muted" style={{ fontSize: "0.7rem" }} />
                      <Form.Control
                        size="sm"
                        value={pair.to || ""}
                        placeholder="LT"
                        onChange={e => {
                          const next = (item.aiReplacements || []).map((r, i) =>
                            i === idx ? { ...r, to: e.target.value } : r);
                          updateItem(item.id, { aiReplacements: next }, groupId);
                        }}
                      />
                      <OverlayTrigger placement="top" overlay={<Tooltip>Remove replacement</Tooltip>}>
                        <Button
                          variant="link"
                          className="text-danger p-0"
                          style={{ fontSize: "0.8rem" }}
                          onClick={() => {
                            const next = (item.aiReplacements || []).filter((_, i) => i !== idx);
                            updateItem(item.id, { aiReplacements: next.length ? next : undefined }, groupId);
                          }}
                        >
                          <i className="fa-regular fa-trash-can" />
                        </Button>
                      </OverlayTrigger>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => updateItem(
                      item.id,
                      { aiReplacements: [...(item.aiReplacements || []), { from: "", to: "" }] },
                      groupId,
                    )}
                  >
                    + Add replacement
                  </Button>
                  <small className="text-muted d-block mt-1" style={{ fontSize: "0.72rem" }}>
                    The speaker dictates naturally and the note reads with your shorthand.
                  </small>
                </Form.Group>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Modal
      fullscreen
      show={show}
      onHide={handleCloseGuard}
      backdrop="static"
      keyboard={false}
    >
      {/* ── Header ── */}
      <Modal.Header closeButton={false} className="py-2 px-3 border-bottom">
        <div className="d-flex w-100 align-items-center gap-2">
          <Button
            variant="link"
            className="text-secondary p-0 text-decoration-none"
            onClick={handleCloseGuard}
            disabled={!!saving}
          >
            <i className="fas fa-arrow-left me-1" />Back
          </Button>

          <div className="flex-grow-1 mx-2" style={{ minWidth: 0 }}>
            <Form.Control
              type="text"
              placeholder="Template Title (required)"
              value={meta.templateTitle}
              onChange={e => setMeta(m => ({ ...m, templateTitle: e.target.value }))}
              className="border-0 border-bottom fw-semibold rounded-0 shadow-none"
              style={{ background: "transparent", fontSize: "1.1rem" }}
              isInvalid={!!errors.templateTitle}
            />
            {errors.templateTitle && (
              <div className="text-danger" style={{ fontSize: "0.75rem" }}>{errors.templateTitle}</div>
            )}
          </div>

          <Button
            variant="outline-secondary"
            onClick={handleSaveDraft}
            disabled={!!saving}
            className="px-3"
          >
            {saving === "draft"
              ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Saving…</>
              : "Save Draft"}
          </Button>
          <Button
            variant="primary"
            onClick={handlePublish}
            disabled={!!saving}
            className="px-3"
          >
            {saving === "publish"
              ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Publishing…</>
              : "Publish"}
          </Button>
        </div>
      </Modal.Header>

      {/* ── Alerts ── */}
      {closeAlert && (
        <Alert variant="info" className="d-flex align-items-center justify-content-between fs-6 fw-normal custom-alert">
          <span>Are you sure you want to close? Changes will not be saved.</span>
          <div className="ms-auto d-flex">
            <Button variant="outline-primary" className="me-1" onClick={confirmClose}><FaCheck /></Button>
            <Button variant="outline-secondary" onClick={() => setCloseAlert(false)}><FaTimes /></Button>
          </div>
        </Alert>
      )}

      {successAlert && (
        <Alert variant="success" className="d-flex align-items-center fs-6 fw-normal custom-alert">
          {successAlert}
        </Alert>
      )}

      {errors._general && (
        <Alert variant="danger" className="d-flex align-items-center fs-6 fw-normal custom-alert">
          {errors._general}
        </Alert>
      )}

      {/* ── Body ── */}
      <Modal.Body className="p-0 d-flex flex-column overflow-hidden" style={{ flex: "1 1 0", minHeight: 0 }}>

        {/* ── Metadata sub-bar ── */}
        <div className="tb-meta-bar">
          <div className="d-flex gap-2 align-items-start flex-grow-1">
            <div style={{ minWidth: 0, flex: "1 1 180px" }}>
              <Form.Control
                as="textarea"
                rows={1}
                size="sm"
                value={meta.templateDescription}
                onChange={e => setMeta(m => ({ ...m, templateDescription: e.target.value }))}
                isInvalid={!!errors.templateDescription}
                placeholder="Description (required)"
              />
              {errors.templateDescription && (
                <div className="text-danger" style={{ fontSize: "0.72rem" }}>{errors.templateDescription}</div>
              )}
            </div>

            <div style={{ flexShrink: 0 }}>
              <small className="text-muted mb-0 flex-shrink-0" style={{ display: "block", fontSize: "0.7rem" }}>Status</small>
              <Form.Select
                size="sm"
                value={meta.templateStatus}
                onChange={e => setMeta(m => ({ ...m, templateStatus: e.target.value }))}
                style={{ minWidth: "110px" }}
              >
                <option value="Draft">Draft</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </Form.Select>
            </div>

            <div style={{ flexShrink: 0 }}>
              <small className="text-muted mb-0 flex-shrink-0" style={{ display: "block", fontSize: "0.7rem" }}>Type</small>
              <Form.Select
                size="sm"
                value={meta.templateInitial}
                onChange={e => setMeta(m => ({ ...m, templateInitial: e.target.value }))}
                style={{ minWidth: "150px" }}
              >
                <option value="initial">Initial Assessment</option>
                <option value="followup">Follow Up</option>
                <option value="reportexport">Report Export</option>
                <option value="intake">Intake</option>
              </Form.Select>
            </div>

            <div style={{ flexShrink: 0 }}>
              <small className="text-muted mb-0 flex-shrink-0" style={{ display: "block", fontSize: "0.7rem" }}>Language</small>
              <Form.Select
                size="sm"
                value={meta.templateLanguage || "en"}
                onChange={e => setMeta(m => ({ ...m, templateLanguage: e.target.value }))}
                style={{ minWidth: "120px" }}
              >
                <option value="en">English</option>
                <option value="af">Afrikaans</option>
              </Form.Select>
            </div>

            <div style={{ flexShrink: 0 }}>
              <small className="text-muted mb-0 flex-shrink-0" style={{ display: "block", fontSize: "0.7rem" }}>Disciplines</small>
              <Dropdown autoClose="outside">
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="tb-disciplines-toggle"
                  style={{ minWidth: "150px" }}
                >
                  {disciplineSummary}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="shadow-sm p-2"
                  style={{ minWidth: 280, maxHeight: 300, overflowY: "auto" }}
                >
                  <Form.Check
                    type="checkbox"
                    id="tb-disc-all"
                    label="All disciplines"
                    className="fw-semibold mb-1"
                    checked={isAllDisciplines}
                    onChange={() => {
                      setDisciplinesTouched(true);
                      setMeta(m => ({ ...m, templateDisciplines: [] }));
                    }}
                  />
                  <Dropdown.Divider className="my-1" />
                  {disciplineOptions.length === 0 ? (
                    <span className="small text-muted">Loading disciplines…</span>
                  ) : (
                    disciplineOptions.map(d => (
                      <Form.Check
                        key={d.disciplineCode}
                        type="checkbox"
                        id={`tb-disc-${d.disciplineCode}`}
                        label={d.disciplineName}
                        className="mb-0"
                        style={{ fontSize: "0.85rem" }}
                        checked={(meta.templateDisciplines || []).some(x => x.disciplineCode === d.disciplineCode)}
                        onChange={() => toggleDiscipline(d)}
                      />
                    ))
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {isLibrary ? (
            <div style={{ flexShrink: 0, maxWidth: 260 }}>
              <small className="text-muted mb-0" style={{ display: "block", fontSize: "0.7rem" }}>Library template</small>
              <span className="small text-muted">
                Belongs to no practice. Practices take a copy by adopting it.
              </span>
            </div>
          ) : (
          <div style={{ flexShrink: 0 }}>
            <small className="text-muted mb-0" style={{ display: "block", fontSize: "0.7rem" }}>Practices</small>
            {pracName.length === 0 && (
              <span className="text-muted small">Loading…</span>
            )}
            <div className="d-flex flex-wrap gap-2">
              {pracName.map(prac => (
                <Form.Check
                  key={prac.practiceNumber}
                  id={`prac-${prac.practiceNumber}`}
                  type="checkbox"
                  label={prac.practiceName}
                  checked={selectedPractices.includes(prac.practiceNumber)}
                  onChange={() => {
                    const updated = selectedPractices.includes(prac.practiceNumber)
                      ? selectedPractices.filter(p => p !== prac.practiceNumber)
                      : [...selectedPractices, prac.practiceNumber];
                    setSelectedPractices(updated);
                  }}
                  className="mb-0"
                  style={{ fontSize: "0.85rem" }}
                />
              ))}
            </div>
            {errors.templatePracticeId && (
              <div className="text-danger" style={{ fontSize: "0.72rem" }}>{errors.templatePracticeId}</div>
            )}
          </div>
          )}
        </div>

        {/* ── 3-panel layout ── */}
        <div className="tb-layout" style={{ flex: "1 1 0", minHeight: 0 }}>

          {/* ── Sidebar ── */}
          <div className="tb-sidebar">
            <p className="tb-sidebar-label">Elements</p>
            {SIDEBAR_ELEMENTS.map(el => (
              <button
                key={el.type}
                className="tb-elem-btn"
                onClick={() => addElement(el.type)}
              >
                <span className="tb-elem-icon">{el.icon}</span>
                {el.label}
              </button>
            ))}

            <p className="tb-sidebar-label mt-3">Containers</p>
            <button className="tb-elem-btn" onClick={addGroup}>
              <span className="tb-elem-icon">□</span>
              Group
            </button>
          </div>

          {/* ── Canvas ── */}
          <div className="tb-canvas">

            {/* Preview toggle */}
            <div className="d-flex align-items-center gap-2 mb-2">
              <small className="fw-semibold text-secondary">Canvas</small>
              <Form.Check
                type="switch"
                id="preview-toggle"
                label="Live Preview"
                checked={showPreview}
                onChange={e => setShowPreview(e.target.checked)}
                className="ms-auto small"
              />
            </div>

            {/* Page tabs */}
            <div className="tb-page-tabs">
              {pages.map((p, idx) => {
                const isEditing = editingPageIdx === idx;
                const commit = () => {
                  renamePage(idx, editingPageDraft);
                  setEditingPageIdx(null);
                };
                return (
                  <span
                    key={p.pageId}
                    className={`tb-page-tab${idx === activePageIdx ? " active" : ""}`}
                    onClick={() => !isEditing && setActivePageIdx(idx)}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setActivePageIdx(idx);
                      setEditingPageDraft(p.pageTitle || "");
                      setEditingPageIdx(idx);
                    }}
                    title={isEditing ? undefined : "Double-click to rename"}
                  >
                    {isEditing ? (
                      <input
                        autoFocus
                        type="text"
                        className="tb-page-tab-input"
                        value={editingPageDraft}
                        onChange={(e) => setEditingPageDraft(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={commit}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); commit(); }
                          else if (e.key === "Escape") { e.preventDefault(); setEditingPageIdx(null); }
                        }}
                        maxLength={80}
                        style={{
                          border: "none",
                          background: "transparent",
                          outline: "none",
                          font: "inherit",
                          color: "inherit",
                          width: `${Math.max(6, (editingPageDraft || "").length + 1)}ch`,
                          padding: 0,
                        }}
                      />
                    ) : (
                      p.pageTitle
                    )}
                    {pages.length > 1 && !isEditing && (
                      <button
                        className="tb-page-tab-close"
                        onClick={e => { e.stopPropagation(); deletePage(idx); }}
                        title="Remove page"
                      >×</button>
                    )}
                  </span>
                );
              })}
              <span className="tb-page-tab" onClick={addPage} style={{ fontWeight: "bold" }}>
                + Add Page
              </span>
            </div>

            {/* Canvas content — DnD edit view or NoteRenderer preview */}
            {showPreview ? (
              <NoteRenderer
                pages={[pages[activePageIdx]].map(page => ({
                  ...page,
                  items: page.items.map(item => {
                    if (item.kind === "group") {
                      return {
                        ...item,
                        children: item.children.map(child =>
                          child.type === "image" && pendingImages[child.id]
                            ? { ...child, imageUrl: pendingImages[child.id].preview }
                            : child
                        ),
                      };
                    }
                    return item.type === "image" && pendingImages[item.id]
                      ? { ...item, imageUrl: pendingImages[item.id].preview }
                      : item;
                  }),
                }))}
                mode="preview"
              />
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={`page-${activePage.pageId}`} direction="vertical">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`tb-page-drop${snapshot.isDraggingOver ? " dragging-over" : ""}`}
                    >
                      {activePage.items.map((item, idx) => (
                        <Draggable key={item.id} draggableId={item.id} index={idx}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className="tb-draggable-item tb-canvas-item"
                              style={{
                                ...provided.draggableProps.style,
                                ...(snapshot.isDragging
                                  ? {}
                                  : { width: item.kind === "group" ? `${((item.colSpan || 12) / 12) * 100}%` : colWidth(item) }),
                              }}
                            >
                              {item.kind === "group"
                                ? renderGroupCard(item, provided.dragHandleProps)
                                : renderElementCard(item, provided.dragHandleProps, null)
                              }
                            </div>
                          )}
                        </Draggable>
                      ))}

                      {provided.placeholder}

                      {activePage.items.length === 0 && !snapshot.isDraggingOver && (
                        <div className="text-center py-5 text-muted tb-empty-canvas">
                          <p className="mb-1">
                            <i className="fas fa-mouse-pointer me-1" />
                            Click an element in the sidebar to get started.
                          </p>
                          <p className="small mb-0">
                            Elements appear here. Drag a block to reorder it.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>

          {/* ── Properties panel (right) ── */}
          <div className="tb-props-panel">
            {renderPropertiesPanel({
              selected: selectedItem,
              updateItem,
              deleteItem,
              handlePendingImage,
              pendingImages,
            })}
          </div>

        </div>
      </Modal.Body>
    </Modal>
  );
}
